import {
  Box,
  Button,
  FormControl,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import DataTable, { DataTableFilter, DataTableFilterItem } from "../components/DataTable";
import Subheader from "../components/Subheader";
import Api from "../openapi";
import { DeliveryPackageByDriverResponse, DeliveryPackageResponse } from "../openapi/client";
import { GridPaginationModel, GridCallbackDetails, GridSortModel, GridRenderCellParams } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import html2pdf from "html2pdf.js";
import html2canvas from "html2canvas";
import { DownloadIcon } from "../assets/icons";
import { ChannelIcon, checkEmpty, defaultFormat, displayFormat } from "../utils";
import useZones from "../hooks/useZones";
import useAreasBrief from "../hooks/useAreaBrief";
import useDrivers from "../hooks/useDrivers";
import Can from "../components/Can";
import { getUserNameWithIcon } from "./Users";

export default function DeliveryReport() {
  const [selectedDate, setSelectedDate] = useState(moment());
  const [packages, setPackages] = useState<any[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloadProcess, setDownloadProcess] = useState(false);
  const reference = useRef<HTMLElement>();

  const zones = useZones();
  const areas = useAreasBrief();
  const drivers = useDrivers();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: "user.fullName",
      sort: "asc",
    },
  ]);

  const [filterModel, setFilterModel] = useState<DataTableFilter>({
    area: null,
    zone: null,
    driverId: null,
  });

  const filterItems: DataTableFilterItem[] = [
    {
      field: "area",
      label: "Area",
      type: "select",
      listData: areas,
    },
    {
      field: "zone",
      label: "Zone",
      type: "select",
      listData: zones,
    },
    {
      field: "driverId",
      label: "Driver",
      type: "select",
      listData: drivers,
    },
  ];

  const handlePaginationChange = (model: GridPaginationModel, details: GridCallbackDetails<any>) => {
    setPaginationModel(model);
  };
  const handleSortChange = (sortModel: GridSortModel) => {
    sortModel.length && setSortModel(sortModel);
  };

  const toAddressString = (address: any) => {
    var str = [];

    if (address.area) {
      str.push("AREA " + address.area);
    }
    if (address.zone) {
      str.push("ZONE " + address.zone);
    }
    if (address.street) {
      str.push("ST. " + address.street);
    }

    if (address.building) {
      str.push("BN. " + address.building);
    }

    if (address.flat) {
      str.push("FL. " + address.flat);
    }

    return str.join(", ");
  };

  const pdfHandler = async () => {
    let opt = {
      margin: 0,
      filename: `deliveryReport-${selectedDate.format(defaultFormat)}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: {
        width: 1240,
      },
      jsPDF: {
        unit: "px",
        format: [1240, 1754],
        hotfixes: ["px_scaling"],
      },
    };

    setDownloadProcess(true);

    try {
      const res = await Api.Delivery.listPackagesByDrivers(selectedDate.format(defaultFormat));
      setReport(res.data);
      setTimeout(() => {
        html2pdf().set(opt).from(reference.current).save();
      });
    } catch (error) {}
    setDownloadProcess(false);
  };

  const columns = useMemo(() => {
    return [
      {
        field: "date",
        sortable: false,
        minWidth: 80,
        flex: 0,
        headerName: "Date",
        valueGetter: (params: any) => selectedDate.format(displayFormat),
      },
      {
        field: "user.externalId",
        minWidth: 110,
        flex: 0,
        headerName: "Member ID",
        valueGetter: (params: any) => params.row.user.externalId,
      },
      {
        field: "user.fullName",
        minWidth: 130,
        flex: 1,
        headerName: "Member Name",
        renderCell: (params: any) => getUserNameWithIcon(params.row.user.fullName, params.row.user.channel),
      },
      {
        field: "user.phoneNumber",
        minWidth: 130,
        flex: 0,
        headerName: "Member Phone",
        valueGetter: (params: any) => params.row.user.phoneNumber,
      },
      {
        field: "dietitianFullName",
        minWidth: 130,
        flex: 1,
        headerName: "Dietitian",
        valueGetter: (params: any) => params.row.dietitianFullName || "-",
      },
      {
        field: "driver.fullName",
        sortable: false,
        minWidth: 80,
        flex: 1,
        headerName: "Delivery Driver",
        valueGetter: (params: any) => params.row.user.driver?.fullName,
      },
      {
        field: "user.address.detail",
        sortable: false,
        minWidth: 80,
        flex: 2,
        headerName: "Delivery Address",
        renderCell: (params: GridRenderCellParams<any, any>) => (
          <Box display="flex" minHeight="45px" height="100%" alignItems="center">
            <Box display="flex" flexDirection="column">
              <Box>{toAddressString(params.row.user.address)}</Box>
              {params.row.user.address.detail ? (
                <Box sx={{ whiteSpace: "pre-line" }}>{params.row.user.address.detail}</Box>
              ) : null}
            </Box>
          </Box>
        ),
      },
      {
        field: "deliveryNote",
        minWidth: 80,
        flex: 2,
        headerName: "Delivery Note",
        valueGetter: (params: any) => params.row.deliveryNote,
      },
    ];
  }, [packages, selectedDate]);

  useEffect(() => {
    const getPackages = async () => {
      setLoading(true);
      try {
        const res = await Api.Delivery.listPackagesByDate(
          selectedDate.format(defaultFormat),
          filterModel.area,
          filterModel.zone,
          filterModel.driverId,
          paginationModel.page,
          paginationModel.pageSize,
          sortModel.map((i) => `${i.field},${i.sort}`)
        );
        var packages = res.data.content as DeliveryPackageResponse[];
        packages = packages.map((p, ind) => {
          var t = p as any;
          t.id = ind;
          return t;
        });
        setPackages(packages);
        setTotalRowCount(Number(res.data.totalElements));
      } catch (error) {}
      setLoading(false);
    };
    getPackages();
  }, [paginationModel, sortModel, selectedDate, filterModel]);

  // Delivery report
  const [report, setReport] = useState<DeliveryPackageByDriverResponse[]>();

  return (
    <>
      <Box className="delivery-report">
        <Box className="delivery-report-upper">
          <Box className="delivery-report-upper-left">
            <Subheader page="Delivery Report" />
          </Box>
          <Box className="delivery-report-upper-right">
            <Box className="delivery-report-selection-wrapper">
              <Box sx={{ width: 250 }}>
                <FormControl fullWidth>
                  <DatePicker
                    className="delivery-report-date-selector"
                    sx={{ "& .MuiOutlinedInput-root": { height: "44px" }, "& fieldset": { border: "none" } }}
                    value={selectedDate}
                    onChange={(date) => setSelectedDate(moment(date))}
                    format="DD/MM/YYYY"
                  />
                </FormControl>
              </Box>
            </Box>
            <Can permission="DELIVERY_REPORT_CREATE">
              <Button
                onClick={pdfHandler}
                disabled={downloadProcess}
                sx={{
                  "&, &:hover": { boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)" },
                  background: "#fff",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "normal",
                  display: "flex",
                  padding: "12px",
                  "&, & svg": { color: "text.dark2" },
                  "&:hover": { background: "#fff" },
                }}
                endIcon={<DownloadIcon />}
              >
                Download
              </Button>
            </Can>
          </Box>
        </Box>

        <Box p={6} className="delivery-report-container">
          <DataTable
            rows={packages}
            rowCount={totalRowCount}
            columns={columns}
            sortingMode="server"
            sortModel={sortModel}
            paginationMode="server"
            paginationModel={paginationModel}
            paginationChange={handlePaginationChange}
            sortChange={handleSortChange}
            filterItems={filterItems}
            filterModel={filterModel}
            filterModelChange={setFilterModel}
            loading={loading}
            getRowHeight={() => "auto"}
            options={{
              hideActions: true,
              entityName: "package",
              filter: true,
              permission: {
                create: "DELIVERY_REPORT_CREATE",
                edit: "DELIVERY_REPORT_EDIT",
                delete: "DELIVERY_REPORT_DELETE",
              },
            }}
          />
        </Box>
      </Box>
      <Box position={"absolute"} left="-1111111px">
        <Box ref={reference} className="print" sx={{ width: "100%", background: "#fff" }}>
          {report?.map((section, key) => {
            return (
              <Box
                key={key}
                className="html2pdf__page-break"
                sx={{ display: "flex", flexDirection: "column", rowGap: "32px", padding: "32px" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", columnGap: "32px" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <img src="../../logo.png" />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      rowGap: "12px",
                      padding: "0 32px",
                      borderLeft: "2px solid #000",
                    }}
                  >
                    <Typography sx={{ color: "#000", fontSize: "20px", fontWeight: 700, lineHeight: "normal" }}>
                      Daily Driver List
                    </Typography>
                    <Typography sx={{ color: "#000", fontSize: "26px", fontWeight: 700, lineHeight: "normal" }}>
                      {selectedDate.format("dddd, D MMMM, YYYY")}
                    </Typography>
                    <Box sx={{ display: "flex", columnGap: "32px", alignItems: "center" }}>
                      <Typography sx={{ color: "#000", fontSize: "20px", fontWeight: 500, lineHeight: "normal" }}>
                        Special Driver: <b>{section.driver?.fullName}</b>
                      </Typography>
                      <Typography sx={{ color: "#000", fontSize: "20px", fontWeight: 500, lineHeight: "normal" }}>
                        Total # of Baskets: <b>{section.members?.length}</b>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <TableContainer
                  component={Paper}
                  sx={{ borderRadius: "24px", border: "2px solid #575C66", boxShadow: "none" }}
                >
                  <Table
                    sx={{
                      minWidth: 650,
                      pageBreakInside: "auto",
                      "& tr": { pageBreakInside: "avoid", pageBreakAfter: "auto" },
                    }}
                    aria-label="simple table"
                  >
                    <TableHead sx={{ background: "#575C66", border: "2px solid #575C66" }}>
                      <TableRow sx={{ "& th": { border: "none", color: "#fff", fontSize: "20px", fontWeight: 700 } }}>
                        <TableCell>Member Name</TableCell>
                        <TableCell>Address</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {section.members?.map((row) => (
                        <TableRow
                          key={row.phoneNumber}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            padding: "16px 24px",
                            "& th, & td": { borderBottom: "2px dashed #575C66" },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "12px" }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <Typography
                                  sx={{ color: "#000", fontSize: "18px", fontWeight: 600, lineHeight: "normal" }}
                                >
                                  {row.fullName}
                                </Typography>
                                {ChannelIcon(String(row.channel))}
                              </Box>
                              {!checkEmpty(row.externalId) && (
                                <Typography
                                  sx={{ color: "#000", fontSize: "14px", fontWeight: 600, lineHeight: "normal" }}
                                >
                                  {row.externalId}
                                </Typography>
                              )}
                              <Typography
                                sx={{ color: "#000", fontSize: "18px", fontWeight: 600, lineHeight: "normal" }}
                              >
                                {row.phoneNumber}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                              <Box sx={{ display: "flex", flexDirection: "column", rowGap: "4px" }}>
                                <Typography
                                  sx={{ color: "#000", fontSize: "18px", fontWeight: 700, lineHeight: "normal" }}
                                >
                                  {!checkEmpty(row.address?.area) ? "Area: " + row.address?.area + ", " : ""}
                                  {!checkEmpty(row.address?.zone) ? "Zone: " + row.address?.zone : ""}
                                  {!checkEmpty(row.address?.street) ? ", Street: " + row.address?.street : ""}
                                  {!checkEmpty(row.address?.building) ? ", Building: " + row.address?.building : ""}
                                  {!checkEmpty(row.address?.flat) ? ", Flat: " + row.address?.flat : ""}
                                </Typography>
                                <Typography
                                  sx={{ color: "#000", fontSize: "18px", fontWeight: 600, lineHeight: "normal" }}
                                >
                                  {row.address?.detail}
                                </Typography>
                              </Box>

                              <Box sx={{ marginTop: "10px", display: "flex", alignItems: "center", columnGap: "4px" }}>
                                <Typography
                                  sx={{ color: "text.red", fontSize: "16px", fontWeight: 600, lineHeight: "normal" }}
                                >
                                  Note:
                                </Typography>
                                <Typography
                                  sx={{ color: "#000", fontSize: "16px", fontWeight: 500, lineHeight: "normal" }}
                                >
                                  {row.deliveryNote || "-"}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );
}
